import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getTopicCluster, type TopicCluster, type ClusterKind } from "@/lib/taxonomy.functions";
import { getTopic } from "@/lib/taxonomy";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { breadcrumbs, itemList, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const KIND_LABEL: Record<ClusterKind, string> = {
  release: "Music",
  book: "Books",
  lesson: "Academy lessons",
  activation: "Activations",
  wisdom: "Wisdom",
  news: "News",
  product: "Products",
};

const KIND_ORDER: ClusterKind[] = ["lesson", "release", "book", "activation", "wisdom", "news", "product"];

export const Route = createFileRoute("/topics/$slug")({
  loader: async ({ params }) => {
    const topic = await getTopicCluster({ data: { slug: params.slug } });
    if (!topic) throw notFound();
    return { topic };
  },
  component: TopicHub,
  head: ({ params, loaderData }) => {
    const t = loaderData?.topic;
    if (!t) return {};
    const title = `${t.name} | Buddha KA & KAfrequency`;
    const description = `${t.definition} ${t.total} works across music, books, Academy lessons, activations, and writings.`.slice(0, 158);
    return {
      ...seoMeta({ title, description, path: `/topics/${params.slug}` }),
      links: [{ rel: "canonical", href: absoluteUrl(`/topics/${params.slug}`) }],
      scripts: [
        ldScript(
          pageNode({
            type: "CollectionPage",
            id: `${SITE_URL}/topics/${params.slug}#topic`,
            url: `${SITE_URL}/topics/${params.slug}`,
            name: t.name,
            description: t.description,
            about: [personRef(), orgRef()],
            extra: {
              publisher: orgRef(),
              ...(t.items.length
                ? { mainEntity: itemList(t.items.map((i) => ({ path: i.path, name: i.title }))) }
                : {}),
            },
          }),
        ),
        ldScript({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: `What does ${t.name.toLowerCase()} mean in the KAfrequency canon?`,
              acceptedAnswer: { "@type": "Answer", text: t.definition },
            },
            {
              "@type": "Question",
              name: `Where can I study ${t.name.toLowerCase()} with Buddha KA?`,
              acceptedAnswer: {
                "@type": "Answer",
                text: `${t.total} first-party works are indexed under ${t.name} — Academy lessons, activations, music releases, books, and writings, all listed at ${SITE_URL}/topics/${t.slug}.`,
              },
            },
          ],
        }),
        ldScript(breadcrumbs([["Topics", "/topics"], [t.name, `/topics/${params.slug}`]])),
      ],
    };
  },
});

function TopicHub() {
  const { topic } = Route.useLoaderData() as { topic: TopicCluster };
  const meta = getTopic(topic.slug);
  const intro = meta?.intro ?? [];
  const hubLinks = meta?.hubLinks ?? [];

  return (
    <article className="mx-auto max-w-5xl px-4 py-20 sm:px-6">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-[0.3em] text-gold">
        <Link to="/topics" className="hover:text-gold-luminous">Topics</Link>
      </nav>
      {meta?.territory && (
        <p className="mt-4 text-xs uppercase tracking-[0.3em] text-muted-foreground">
          Authority territory
        </p>
      )}
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl md:text-6xl">{topic.name}</h1>
      <p className="mt-4 max-w-2xl text-lg text-ivory/90">{topic.definition}</p>
      <p className="mt-3 max-w-2xl text-muted-foreground">{topic.description}</p>

      {intro.length > 0 && (
        <div className="mt-8 max-w-2xl space-y-4 text-lg leading-relaxed text-ivory/85">
          {intro.map((para) => (
            <p key={para.slice(0, 24)}>{para}</p>
          ))}
        </div>
      )}

      {hubLinks.length > 0 && (
        <nav aria-label="Ecosystem destinations" className="mt-10 rounded-2xl border hairline bg-charcoal/50 p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Enter the work</p>
          <ul className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            {hubLinks.map((l) => (
              <li key={l.to}>
                <a
                  href={l.to}
                  className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
                >
                  {l.label}
                </a>
                {l.note && <span className="text-muted-foreground"> — {l.note}</span>}
              </li>
            ))}
          </ul>
        </nav>
      )}

      {topic.total === 0 && (
        <p className="mt-16 text-muted-foreground">
          No published work carries this topic yet. It will appear here the moment one does.
        </p>
      )}

      {KIND_ORDER.map((kind) => {
        const items = topic.items.filter((i) => i.kind === kind);
        if (!items.length) return null;
        return (
          <section key={kind} className="mt-14" aria-labelledby={`topic-${kind}`}>
            <h2 id={`topic-${kind}`} className="font-serif text-2xl text-gold-luminous">
              {KIND_LABEL[kind]}
              <span className="ml-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {topic.counts[kind]}
              </span>
            </h2>
            <ul className="mt-5 grid gap-4 sm:grid-cols-2">
              {items.map((i) => (
                <li key={`${i.kind}:${i.slug}`} className="rounded-xl border hairline bg-charcoal/50 p-5">
                  <a href={i.path} className="font-serif text-lg text-ivory hover:text-gold-luminous">
                    {i.title}
                  </a>
                  {i.subtitle && (
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{i.subtitle}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </article>
  );
}
